import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react'

import { STORAGE_KEYS } from '../shared/lib/constants'
import { queryClient } from '../shared/api/query-client'
import { setSentryUser } from '../shared/observability/sentry'

export interface AuthUser {
  id: number | null
  email: string | null
  name: string
  role: string
  active: boolean
  doctor_id?: number | null
}

interface AuthState {
  token: string | null
  user: AuthUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<AuthUser>
  /** Sessão emitida fora do login (link de convite/redefinição, troca de senha). */
  adoptSession: (session: { token: string; user: AuthUser }) => void
  logout: () => void
}

const AuthContext = createContext<AuthState | null>(null)

async function loginErrorMessage(res: Response): Promise<string> {
  const contentType = res.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    const err = (await res.json().catch(() => null)) as { detail?: string } | null
    if (err?.detail) return err.detail
  }

  const text = (await res.text().catch(() => '')).trim()
  if (res.status >= 500) {
    return `API indisponível ou com erro (${res.status}).`
  }
  return text || `Falha no login (${res.status}).`
}

function readUserFromStorage(): AuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER)
    return raw ? (JSON.parse(raw) as AuthUser) : null
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(STORAGE_KEYS.TOKEN))
  const [user, setUser] = useState<AuthUser | null>(() => readUserFromStorage())
  const [loading, setLoading] = useState<boolean>(!!token)

  // Validate token once on mount.
  useEffect(() => {
    if (!token) {
      return
    }

    fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Invalid token')
        return res.json() as Promise<AuthUser>
      })
      .then((data) => {
        setUser(data)
        setSentryUser(data)
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data))
      })
      .catch(() => {
        queryClient.clear()
        localStorage.removeItem(STORAGE_KEYS.TOKEN)
        localStorage.removeItem(STORAGE_KEYS.USER)
        setToken(null)
        setUser(null)
        setSentryUser(null)
      })
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const adoptSession = useCallback((session: { token: string; user: AuthUser }) => {
    // Never reuse another session's reports, permissions or in-flight queries.
    queryClient.clear()
    localStorage.setItem(STORAGE_KEYS.TOKEN, session.token)
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(session.user))
    setToken(session.token)
    setUser(session.user)
    setSentryUser(session.user)
  }, [])

  const login = useCallback(async (email: string, password: string): Promise<AuthUser> => {
    let res: Response
    try {
      res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
    } catch {
      throw new Error('API indisponível. Verifique se o backend local está rodando.')
    }
    if (!res.ok) {
      throw new Error(await loginErrorMessage(res))
    }
    const data = (await res.json()) as { token: string; user: AuthUser }
    adoptSession(data)
    return data.user
  }, [adoptSession])

  const logout = useCallback(() => {
    queryClient.clear()
    // Revoga no servidor (bump da versão de sessão) — fire-and-forget: a
    // limpeza local acontece mesmo se a rede falhar.
    const current = localStorage.getItem(STORAGE_KEYS.TOKEN)
    if (current) {
      void fetch('/api/auth/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${current}` },
      }).catch(() => {})
    }
    localStorage.removeItem(STORAGE_KEYS.TOKEN)
    localStorage.removeItem(STORAGE_KEYS.USER)
    setToken(null)
    setUser(null)
    setSentryUser(null)
  }, [])

  return (
    <AuthContext.Provider value={{ token, user, loading, login, adoptSession, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside AuthProvider')
  return ctx
}
